import React, { useEffect, useState } from 'react';
import { Trash2, CornerDownRight, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { FOLLOWUP_FIELD_TYPES, optionLabel, sameOption } from '@/lib/choiceFollowups';

/* Survey Builder: per-answer-option conditional follow-up (single choice). */

const newFollowup = (option) => ({ option, label: 'Please specify', field_type: 'short_text', required: false, min_value: null, max_value: null, choices: [] });
const toNum = (s) => (String(s).trim() === '' || Number.isNaN(Number(s)) ? null : Number(s));

function useDraft(value) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return [draft, setDraft];
}

function NumberBounds({ f, onSave, i }) {
  const [min, setMin] = useDraft(f.min_value ?? '');
  const [max, setMax] = useDraft(f.max_value ?? '');
  const field = (val, set, key, label) => (
    <div>
      <label className="text-[10px] font-medium text-[#64748B] block mb-1">{label}</label>
      <Input type="number" value={val} onChange={(e) => set(e.target.value)} onBlur={() => toNum(val) !== (f[key] ?? null) && onSave({ [key]: toNum(val) })}
        placeholder="No limit" className="text-xs h-8" data-testid={`followup-${key}-${i}`} />
    </div>
  );
  return <div className="grid grid-cols-2 gap-2">{field(min, setMin, 'min_value', 'Minimum')}{field(max, setMax, 'max_value', 'Maximum')}</div>;
}

function DropdownChoices({ f, onSave, i }) {
  const joined = (f.choices || []).join('\n');
  const [text, setText] = useDraft(joined);
  const commit = () => {
    const list = text.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!list.length) { setText(joined); return; }
    if (list.join('\n') !== joined) onSave({ choices: list });
  };
  return (
    <div>
      <label className="text-[10px] font-medium text-[#64748B] block mb-1">Dropdown choices (one per line)</label>
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} onBlur={commit} className="text-xs" data-testid={`followup-choices-${i}`} />
    </div>
  );
}

function FollowupCard({ f, i, onSave, onRemove }) {
  const [label, setLabel] = useDraft(f.label);
  const setType = (t) => onSave({ field_type: t, ...(t === 'dropdown' && !(f.choices || []).length ? { choices: ['Option 1', 'Option 2'] } : {}) });
  return (
    <div className="mt-2 ml-3 pl-3 border-l-2 border-[#1A3E6F] space-y-2.5" data-testid={`followup-config-${i}`}>
      <div>
        <label className="text-[10px] font-medium text-[#64748B] block mb-1">Follow-up label</label>
        <Input value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)}
          onBlur={() => { const t = label.trim(); if (!t) setLabel(f.label); else if (t !== f.label) onSave({ label: t }); }}
          className="text-xs h-8" data-testid={`followup-label-input-${i}`} />
      </div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="text-[10px] font-medium text-[#64748B] block mb-1">Field type</label>
          <select value={f.field_type} onChange={(e) => setType(e.target.value)} className="w-full border border-[#E2E8F0] rounded-md px-2 h-8 text-xs bg-white" data-testid={`followup-type-${i}`}>
            {FOLLOWUP_FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-[#64748B] pb-1.5">
          Required <Switch checked={!!f.required} onCheckedChange={(v) => onSave({ required: v })} data-testid={`followup-required-${i}`} />
        </label>
      </div>
      {f.field_type === 'number' && <NumberBounds f={f} onSave={onSave} i={i} />}
      {f.field_type === 'dropdown' && <DropdownChoices f={f} onSave={onSave} i={i} />}
      <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 text-[11px] text-rose-600 hover:underline" data-testid={`followup-remove-${i}`}>
        <Trash2 className="w-3 h-3" /> Remove follow-up
      </button>
    </div>
  );
}

export default function ChoiceFollowupSettings({ question, onUpdate }) {
  const options = (question.options || []).map(optionLabel).filter(Boolean);
  const list = question.option_followups || [];
  const orphans = list.filter((f) => !options.some((o) => sameOption(o, f.option)));
  const find = (opt) => list.find((f) => sameOption(f.option, opt));
  const save = (next) => onUpdate(question.id, {
    option_followups: options.map((o) => next.find((f) => sameOption(f.option, o))).filter(Boolean),
  });
  const upsert = (opt, patch) => save([...list.filter((f) => !sameOption(f.option, opt)), { ...(find(opt) || newFollowup(opt)), ...patch }]);
  const remove = (opt) => save(list.filter((f) => !sameOption(f.option, opt)));
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="followup-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Conditional follow-ups</h4>
      <p className="text-[10px] text-[#94A3B8]">Show an extra field directly below an answer when that answer is selected. Same screen, no extra step.</p>
      {options.map((opt, i) => {
        const f = find(opt);
        return (
          <div key={opt} className={`rounded-lg border p-2.5 ${f ? 'border-[#1A3E6F] bg-[#F5F8FC]' : 'border-[#E2E8F0]'}`} data-testid={`followup-option-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-[#0F172A] leading-snug break-words">
                {f && <span className="mr-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-white bg-[#1A3E6F] rounded px-1.5 py-0.5" data-testid={`followup-trigger-badge-${i}`}><CornerDownRight className="w-2.5 h-2.5" />Trigger</span>}
                {opt}
              </p>
              {!f && <button type="button" onClick={() => upsert(opt, {})} className="shrink-0 text-[11px] text-[#1A3E6F] font-semibold hover:underline" data-testid={`followup-add-${i}`}>+ Add follow-up</button>}
            </div>
            {f && <FollowupCard f={f} i={i} onSave={(patch) => upsert(opt, patch)} onRemove={() => remove(opt)} />}
          </div>
        );
      })}
      {orphans.length > 0 && (
        <p className="text-[10px] text-amber-700 flex items-start gap-1" data-testid="followup-orphans">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          {orphans.length} follow-up(s) belong to an answer that no longer exists. They are not shown to respondents and will be removed on the next save.
        </p>
      )}
    </div>
  );
}
